import Chart from "chart.js/auto";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { ZeosButton } from "../../components/Shared/ZeosButton";
import { ZeosInput } from "../../components/Shared/ZeosInput";
import { ZeosSelect } from "../../components/Shared/ZeosSelect";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import {
  IDerapiSiteIntervalsResponse,
  IDerapiSolarInverterIntervalsResponse,
} from "../../services/zeos-api/contracts/Derapi";

const SUMMARY_LEVEL_OPTIONS = ["month", "day", "hour", "15mins"];
const MISSING_OPTIONS = ["include", "drop"];

const buildDefaultStart = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return date.toISOString();
};

export const DerapiTestScreen = () => {
  const api = useContext(ZeosApiContext);
  const hasAutoLoaded = useRef(false);
  // Note for matt.banks@rewiregroup.net: auto-loads Rockcity Derapi data on entry for quick validation.
  const siteChartRef = useRef<HTMLCanvasElement | null>(null);
  const inverterChartRef = useRef<HTMLCanvasElement | null>(null);
  const siteChartInstance = useRef<Chart | null>(null);
  const inverterChartInstance = useRef<Chart | null>(null);

  const [summaryLevel, setSummaryLevel] = useState("day");
  const [missing, setMissing] = useState("include");
  const [start, setStart] = useState(buildDefaultStart());
  const [end, setEnd] = useState(new Date().toISOString());
  const [propertyId, setPropertyId] = useState("");
  const [siteId, setSiteId] = useState("site-iuxkvipbesgjv3ub");
  const [inverterId, setInverterId] = useState("");

  const [siteLoading, setSiteLoading] = useState(false);
  const [inverterLoading, setInverterLoading] = useState(false);
  const [siteError, setSiteError] = useState("");
  const [inverterError, setInverterError] = useState("");
  const [siteData, setSiteData] =
    useState<IDerapiSiteIntervalsResponse | null>(null);
  const [inverterData, setInverterData] = useState<
    IDerapiSolarInverterIntervalsResponse[]
  >([]);

  const queryParams = useMemo(() => {
    return {
      summaryLevel,
      missing,
      start,
      end,
      propertyId: propertyId ? Number(propertyId) : undefined,
      siteId: siteId || undefined,
      inverterId: inverterId || undefined,
    };
  }, [summaryLevel, missing, start, end, propertyId, siteId, inverterId]);

  const handleLoadSite = async () => {
    setSiteLoading(true);
    setSiteError("");
    setSiteData(null);
    const response = await api.Derapi.getRockcitySiteIntervals(queryParams);
    if (response.zeosStatus?.toLowerCase() !== "success") {
      setSiteError(response.message || "Failed to load site intervals.");
      setSiteLoading(false);
      return;
    }
    setSiteData(response.data);
    setSiteLoading(false);
  };

  const handleLoadInverters = async () => {
    setInverterLoading(true);
    setInverterError("");
    setInverterData([]);
    const response =
      await api.Derapi.getRockcityInverterIntervals(queryParams);
    if (response.zeosStatus?.toLowerCase() !== "success") {
      setInverterError(response.message || "Failed to load inverter intervals.");
      setInverterLoading(false);
      return;
    }
    setInverterData(response.data || []);
    setInverterLoading(false);
  };

  const buildSeries = (intervals: { start: string; kwh?: number | null }[]) => {
    const rows = intervals
      .filter((interval) => interval.start)
      .map((interval) => ({
        label: interval.start,
        value: interval.kwh ?? 0,
      }))
      .sort((a, b) => new Date(a.label).getTime() - new Date(b.label).getTime());
    return {
      labels: rows.map((row) => row.label),
      values: rows.map((row) => row.value),
    };
  };

  useEffect(() => {
    if (hasAutoLoaded.current) {
      return;
    }
    hasAutoLoaded.current = true;
    handleLoadSite();
    handleLoadInverters();
  }, []);

  useEffect(() => {
    if (!siteChartRef.current) {
      return;
    }
    if (siteChartInstance.current) {
      siteChartInstance.current.destroy();
      siteChartInstance.current = null;
    }
    if (!siteData || siteData.intervals.length === 0) {
      return;
    }
    const ctx = siteChartRef.current.getContext("2d");
    if (!ctx) {
      return;
    }
    const series = buildSeries(siteData.intervals);
    siteChartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: series.labels,
        datasets: [
          {
            label: "Site kWh",
            data: series.values,
            borderColor: "#f58821",
            backgroundColor: "rgba(245, 136, 33, 0.15)",
            tension: 0.25,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            ticks: { color: "#475569", maxRotation: 0, autoSkip: true },
            grid: { display: false },
          },
          y: {
            ticks: { color: "#475569" },
            grid: { color: "rgba(148, 163, 184, 0.3)" },
            title: { display: true, text: "kWh" },
          },
        },
      },
    });
  }, [siteData]);

  useEffect(() => {
    if (!inverterChartRef.current) {
      return;
    }
    if (inverterChartInstance.current) {
      inverterChartInstance.current.destroy();
      inverterChartInstance.current = null;
    }
    if (!inverterData || inverterData.length === 0) {
      return;
    }
    const ctx = inverterChartRef.current.getContext("2d");
    if (!ctx) {
      return;
    }
    const primary = inverterData[0];
    if (!primary || primary.intervals.length === 0) {
      return;
    }
    const series = buildSeries(primary.intervals);
    inverterChartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: series.labels,
        datasets: [
          {
            label: "Inverter kWh",
            data: series.values,
            borderColor: "#0f766e",
            backgroundColor: "rgba(15, 118, 110, 0.15)",
            tension: 0.25,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            ticks: { color: "#475569", maxRotation: 0, autoSkip: true },
            grid: { display: false },
          },
          y: {
            ticks: { color: "#475569" },
            grid: { color: "rgba(148, 163, 184, 0.3)" },
            title: { display: true, text: "kWh" },
          },
        },
      },
    });
  }, [inverterData]);

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="mx-auto w-full max-w-6xl px-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-semibold text-slate-900">
              Derapi Rockcity Test
            </h1>
            <p className="text-slate-600">
              Pull site-level and inverter-level solar interval data from the
              new Derapi endpoints.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 p-4">
              <ZeosSelect
                label="Summary Level"
                name="summaryLevel"
                value={summaryLevel}
                options={SUMMARY_LEVEL_OPTIONS}
                onChange={(e) => setSummaryLevel(e.target.value)}
              />
              <ZeosSelect
                label="Missing"
                name="missing"
                value={missing}
                options={MISSING_OPTIONS}
                onChange={(e) => setMissing(e.target.value)}
              />
              <ZeosInput
                label="Start (RFC3339)"
                name="start"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
              <ZeosInput
                label="End (RFC3339)"
                name="end"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <ZeosInput
                label="Property ID (optional)"
                name="propertyId"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              />
              <ZeosInput
                label="Site ID (optional)"
                name="siteId"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
              />
              <ZeosInput
                label="Inverter ID (optional)"
                name="inverterId"
                value={inverterId}
                onChange={(e) => setInverterId(e.target.value)}
              />
              <div className="mt-4 flex flex-wrap gap-3">
                <ZeosButton
                  label={siteLoading ? "Loading Site..." : "Load Site Intervals"}
                  onPress={handleLoadSite}
                  isDisabled={siteLoading}
                  type="button"
                  mode="action"
                />
                <ZeosButton
                  label={
                    inverterLoading
                      ? "Loading Inverters..."
                      : "Load Inverter Intervals"
                  }
                  onPress={handleLoadInverters}
                  isDisabled={inverterLoading}
                  type="button"
                  mode="action"
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-900 p-4 text-slate-100">
              <p className="text-sm uppercase tracking-wide text-slate-400">
                Request Preview
              </p>
              <pre className="mt-3 text-xs whitespace-pre-wrap">
{JSON.stringify(queryParams, null, 2)}
              </pre>
            </div>
          </div>
        </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">
                Site Intervals
              </h2>
              {siteError && (
                <span className="text-sm text-red-600">{siteError}</span>
              )}
            </div>
            {siteData ? (
              <div className="mt-4 space-y-4">
                <div className="h-[240px] rounded-lg border border-slate-200 bg-white p-3">
                  {siteData.intervals.length > 0 ? (
                    <canvas ref={siteChartRef} className="h-full w-full" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">
                      No interval data returned.
                    </div>
                  )}
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                  <p>Site ID: {siteData.id}</p>
                  <p>Summary: {siteData.summaryLevel}</p>
                  <p>
                    Window: {siteData.start} → {siteData.end}
                  </p>
                  <p>Total Intervals: {siteData.intervals.length}</p>
                </div>
                <div className="overflow-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="py-2">Start</th>
                        <th className="py-2">End</th>
                        <th className="py-2">kWh</th>
                        <th className="py-2">Missing</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-800">
                      {siteData.intervals.slice(0, 10).map((interval, idx) => (
                        <tr key={`${interval.start}-${idx}`} className="border-t">
                          <td className="py-2 pr-2">{interval.start}</td>
                          <td className="py-2 pr-2">{interval.end}</td>
                          <td className="py-2 pr-2">
                            {interval.kwh ?? "-"}
                          </td>
                          <td className="py-2">
                            {interval.missing ? "yes" : "no"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-500">
                Load site intervals to see data.
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">
                Inverter Intervals
              </h2>
              {inverterError && (
                <span className="text-sm text-red-600">{inverterError}</span>
              )}
            </div>
            {inverterData.length > 0 ? (
              <div className="mt-4 space-y-4">
                <div className="h-[240px] rounded-lg border border-slate-200 bg-white p-3">
                  {inverterData[0]?.intervals?.length ? (
                    <canvas ref={inverterChartRef} className="h-full w-full" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-500">
                      No interval data returned.
                    </div>
                  )}
                </div>
                {inverterData.slice(0, 2).map((payload) => (
                  <div key={payload.id} className="rounded-lg bg-slate-50 p-3">
                    <p className="text-sm text-slate-700">
                      Inverter ID: {payload.id} · Summary: {payload.summaryLevel}
                    </p>
                    <p className="text-xs text-slate-500">
                      Window: {payload.start} → {payload.end}
                    </p>
                    <div className="mt-3 overflow-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="text-slate-500">
                          <tr>
                            <th className="py-2">Start</th>
                            <th className="py-2">End</th>
                            <th className="py-2">kWh</th>
                            <th className="py-2">Missing</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-800">
                          {payload.intervals.slice(0, 6).map((interval, idx) => (
                            <tr
                              key={`${payload.id}-${interval.start}-${idx}`}
                              className="border-t"
                            >
                              <td className="py-2 pr-2">{interval.start}</td>
                              <td className="py-2 pr-2">{interval.end}</td>
                              <td className="py-2 pr-2">
                                {interval.kwh ?? "-"}
                              </td>
                              <td className="py-2">
                                {interval.missing ? "yes" : "no"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                {inverterData.length > 2 && (
                  <p className="text-xs text-slate-500">
                    Showing first 2 inverters only.
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-500">
                Load inverter intervals to see data.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
